/**
 * Authentication hook
 * Supports multiple auth providers
 */
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { useAuthStore } from '@store/auth.store';
import { useNotification } from '@hooks/useNotification';
import { LoginRequest, RegisterRequest } from '../types/auth.types';
import { authConfig, AuthProvider } from '@core/config/auth.config';

export const useAuth = () => {
  const navigate = useNavigate();
  const { setAuth, clearAuth, setLoggingOut } = useAuthStore();
  const { showSuccess, showError } = useNotification();

  // Local login
  const loginMutation = useMutation({
    mutationFn: (credentials: LoginRequest) => authService.login(credentials),
    onSuccess: (response) => {
      setAuth(response.user, response.accessToken, response.refreshToken);
      showSuccess('Login successful!');
      navigate('/projects');
    },
    onError: (error: any) => {
      showError(error.response?.data?.detail || error.message || 'Login failed');
    },
  });

  // Azure AD login
  const azureLoginMutation = useMutation({
    mutationFn: () => authService.login({} as LoginRequest), // Triggers Azure AD flow
    onSuccess: (response) => {
      setAuth(response.user, response.accessToken, response.refreshToken);
      showSuccess('Signed in with Microsoft successfully!');
      navigate('/projects');
    },
    onError: (error: any) => {
      // Don't show error if user cancelled the popup
      const isUserCancelled = 
        error?.isUserCancelled === true ||
        error?.message === 'USER_CANCELLED' ||
        error?.errorCode === 'user_cancelled' ||
        error?.message?.includes('user_cancelled') ||
        error?.message?.includes('User cancelled');
      
      if (!isUserCancelled) {
        showError(error.response?.data?.detail || error.message || 'Azure AD login failed');
      }
    },
  });

  // Register (local only)
  const registerMutation = useMutation({
    mutationFn: (data: RegisterRequest) => authService.register(data),
    onSuccess: (response) => {
      setAuth(response.user, response.accessToken, response.refreshToken);
      showSuccess('Registration successful!');
      navigate('/projects');
    },
    onError: (error: any) => {
      showError(error.response?.data?.detail || error.message || 'Registration failed');
    },
  });

  // Logout
  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Call authService.logout() to clear Azure AD session
      await authService.logout();
    },
    onSuccess: () => {
      // Clear local auth state
      clearAuth();
      // Force redirect to login page with replace to prevent back navigation
      navigate('/login', { replace: true });
      // Force reload to ensure clean state
      window.location.href = '/login';
    },
    onError: (error: any) => {
      showError(error.response?.data?.detail || error.message || 'Logout failed');
    },
  });

  return {
    login: loginMutation.mutateAsync,
    loginWithAzureAD: azureLoginMutation.mutateAsync,
    register: registerMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
    isLoading:
      loginMutation.isPending ||
      azureLoginMutation.isPending ||
      registerMutation.isPending ||
      logoutMutation.isPending,
    isAzureAD: authConfig.provider === AuthProvider.AZURE_AD,
  };
};
